import React from "react";
import { IconTrash } from "@tabler/icons-react";
import {
  ColumnSuggestionsOUT,
  LogicalOperatorENUM,
  PropertyBrowserTreeNode,
} from "api-client";
import DebouncedInput from "components/DebounceInput";
import DropdownBrowser from "components/DropdownBrowser";
import DropdownWithSearch from "components/DropdownWithSearch";
import SimpleSelect from "components/SimpleSelect";
import {
  LOGICAL_OPERATORS,
  PROPERTY_RULE_OPERATORS,
  PROPERTY_RULE_OPERATORS_NUMERIC,
  PROPERTY_RULE_OPERATORS_TEXTUAL,
} from "../../const";
import { dynamicMEStore } from "store/Mapping/DMEStore";
import { BOOLEAN_OPTIONS } from "utils";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { FilterRule } from ".";
import ComboboxSelect from "components/ComboboxSelect";

interface BooleanOptionProps {
  id: string;
  name: string;
  value: null | boolean;
  value_count: number;
  tooltip?: string;
}

const FilterRow = (props: {
  rule: FilterRule;
  index: number;
  onChangeProperty: (value: PropertyBrowserTreeNode[], index: number) => void;
  items: PropertyBrowserTreeNode[];
  wrapperID: string;
  scrollPos: number;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const shouldHideValueField = ["IS_EMPTY", "IS_NOT_EMPTY"].includes(
    props.rule.operator
  );
  let properties = PROPERTY_RULE_OPERATORS;
  if (props.rule.property) {
    properties = props.rule.property.is_numeric
      ? PROPERTY_RULE_OPERATORS_NUMERIC
      : PROPERTY_RULE_OPERATORS_TEXTUAL;
  }

  const checkPropertyIsBoolean = () => {
    const foundedItem = dynamicMEStore.dynamicColumns.find(
      (item) => item.key_name === props.rule.property?.column_key
    );
    return foundedItem?.type === "BOOLEAN";
  };

  const getBooleanSuggestedValues = () => {
    const values = BOOLEAN_OPTIONS.map((item) => ({
      id: item.name,
      name: isEng ? item.name : item.name_de,
      value: item.value as null | boolean,
      value_count:
        props.rule.suggestedValues?.find((rec) => {
          let recValue = rec.value;
          if (recValue === "True") recValue = true;
          if (recValue === "False") recValue = false;
          return recValue === item.value;
        })?.value_count ?? 0,
    }));
    return values;
  };

  const onChangeTerm = (value: string | number | undefined, index: number) => {
    const rules = [...dynamicMEStore.selectedFilterByItems.rules];
    rules[index].value = String(value);
    dynamicMEStore.updateFilterRules(rules);
  };

  const onChangeLogicalOperator = (value: string | undefined) => {
    dynamicMEStore.setSelectedFilterByItems({
      ...dynamicMEStore.selectedFilterByItems,
      logical_operator: value as LogicalOperatorENUM,
    });
  };

  const onChangeOperator = (value: string | undefined, index: number) => {
    const rules = [...dynamicMEStore.selectedFilterByItems.rules];
    rules[index].operator = String(value);
    dynamicMEStore.updateFilterRules(rules);
  };

  const removeCurrentRow = (index: number) => {
    if (index == 0 && dynamicMEStore.selectedFilterByItems.rules.length <= 1) {
      dynamicMEStore.removeAllFilteringRules();
      return;
    }

    const rules = [...dynamicMEStore.selectedFilterByItems.rules];
    rules.splice(index, 1);
    dynamicMEStore.updateFilterRules(rules);
  };

  const propertyValueSelector = () => {
    if (checkPropertyIsBoolean()) {
      const options = getBooleanSuggestedValues();
      const selectedOption = options.find(
        (rec) => String(rec.value) === props.rule.value
      );
      return (
        <ComboboxSelect<BooleanOptionProps>
          items={options}
          displayKey="name"
          detailKey="value_count"
          selectedOption={selectedOption}
          height="middle"
          handleChange={(selected) => {
            onChangeTerm(String(selected.value), props.index);
            dynamicMEStore.setOpenFilter(false);
          }}
          placeholder={t("commons.selectValue")}
          containerClassName="max-w-[160px] min-w-[160px]"
        />
      );
    }

    if (props.rule.suggestedValues?.length && !shouldHideValueField) {
      return (
        <DropdownWithSearch<ColumnSuggestionsOUT>
          items={props.rule.suggestedValues}
          displayKey="value"
          detailKey="value_count"
          value={props.rule.value}
          noMargin
          height="middle"
          handleSelect={(selected) =>
            onChangeTerm(String(selected.value), props.index)
          }
          labelWithSearch
          placeholder={t("mappingEditor.enterValue")}
          containerClassName="max-w-[160px] min-w-[160px]"
          onChangeSearchTerm={(value) =>
            value && onChangeTerm(value, props.index)
          }
          handleRemoveValue={() => onChangeTerm("", props.index)}
        />
      );
    } else {
      return (
        <DebouncedInput
          containerClassName="mt-0 max-w-[160px] min-w-[160px]"
          height="middle"
          onChange={(value) => onChangeTerm(value, props.index)}
          value={props.rule.value}
          hidden={shouldHideValueField}
          icon={<></>}
          placeholder={t("mappingEditor.enterValue")}
          className="placeholder-gray-500"
        />
      );
    }
  };

  return (
    <div className="flex items-center py-2 px-3 gap-2 min-w-[595px] hover:bg-indigo-100 transition group/filterItem">
      {props.index === 0 ? (
        <span className="min-w-[85px] text-right">
          {t("mappingEditor.when")}
        </span>
      ) : (
        <SimpleSelect
          options={LOGICAL_OPERATORS}
          noMargin
          className="!mb-0 !mt-0"
          current={dynamicMEStore.selectedFilterByItems.logical_operator}
          containerClassName="min-w-[85px]"
          onChange={(value) => onChangeLogicalOperator(value)}
        />
      )}
      <DropdownBrowser<PropertyBrowserTreeNode>
        displayKey={isEng ? "name" : "name_de"}
        detailKey={isEng ? "classification" : "classification_de"}
        handleSelect={(value) => props.onChangeProperty(value, props.index)}
        name="filter_by"
        items={props.items}
        checkedItems={props.rule.property ? [props.rule.property] : []}
        placeholder={t("mappingEditor.findProperty")}
        optionsClassName="!max-w-fit min-w-[246px]"
        containerClassName="ml-1 relative bg-white"
        singleSelect
        hasSelectInput
        selectInputClassName="max-w-[134px] w-[134px]"
        fixedPos
        wrapperID={props.wrapperID}
        leftOffset={110}
        parentScrollTop={props.scrollPos}
        scrollAreaHeight={420}
      />
      {checkPropertyIsBoolean() ? (
        isEng ? (
          properties[0].name
        ) : (
          properties[0].name_de
        )
      ) : (
        <SimpleSelect
          options={properties}
          noMargin
          className="!mb-0 !mt-0 min-w-[120px] max-w-[120px]"
          onChange={(value) => onChangeOperator(value, props.index)}
          current={props.rule.operator}
        />
      )}
      {propertyValueSelector()}
      <IconTrash
        className="cursor-pointer ml-2 transition group-hover/filterItem:text-indigo-700 min-w-[20px]"
        size={20}
        onClick={() => removeCurrentRow(props.index)}
      />
    </div>
  );
};

export default observer(FilterRow);
